import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Camera, FileText, ImagePlus, Loader2, Upload, X } from 'lucide-react';
import type { ComprobanteFormulario } from '../../hooks/useComprobanteFormulario';
import type { ComprobanteReciente, PaginaComprobante, SeleccionComprobante } from '../../types/comprobante';
import { AjusteFotoComprobante } from './AjusteFotoComprobante';
import { ComprobantesRecientesDialog } from './ComprobantesRecientesDialog';
import { Checkbox } from '../ui/Checkbox';
import { ERROR_TEXT, LABEL_TEXT } from '../ui/tokens';
import {
  MAX_BYTES_FOTO_ORIGINAL, MAX_BYTES_PAGINA_PROCESADA, MAX_BYTES_PDF, MAX_PAGINAS_COMPROBANTE,
  clasificarArchivo, pdfProtegido, prepararFotoParaAjuste, type ClaseArchivo,
} from '../../utils/comprobanteImagen';
import { formatCurrency, formatDate } from '../../utils/formatters';

const MENSAJE_FORMATO = 'Formato no admitido. Usa una foto (JPEG, PNG, WebP o HEIC) o un PDF.';
const MENSAJE_FOTO_GRANDE = 'La foto supera los 10 MB. Elige una más pequeña.';
const MENSAJE_PDF_GRANDE = 'El PDF supera los 2 MB. Si es un escaneo, prueba con fotos del documento.';
const MENSAJE_MAX_PAGINAS = 'Máximo 5 páginas. Si el documento es más largo, súbelo como PDF.';
const MENSAJE_FOTO_ILEGIBLE = 'No pudimos procesar esta imagen. Intenta con una foto en formato JPEG o PNG.';
const MENSAJE_PDF_CON_FOTOS = 'Este comprobante ya tiene fotos. Quítalas para subir un PDF.';
const MENSAJE_PAGINA_PESADA = 'La foto quedó muy pesada. Recórtala un poco más o súbela como PDF.';
const ACEPTA = 'image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif,application/pdf';
const BOTON = 'inline-flex items-center justify-center gap-2 min-h-11 min-w-11 px-4 rounded-xl border border-slate-200 text-sm text-slate-700 hover:border-slate-300 disabled:opacity-50';

interface ComprobanteInputProps {
  readonly comprobante: ComprobanteFormulario;
  readonly titulo?: string;
  readonly ayuda?: string;
  readonly deshabilitado?: boolean;
}

let contadorDePaginas = 0;

/** Clave local de una página en pantalla. No usa `crypto.randomUUID`: no existe fuera de un contexto seguro (HTTP). */
function nuevoIdDePagina(): string {
  contadorDePaginas += 1;
  return `pagina-${contadorDePaginas}`;
}

/** Por qué no se puede recibir el archivo; null si sirve. */
function motivoDeRechazo(archivo: File, clase: ClaseArchivo, yaHayFotos: boolean): string | null {
  if (clase === 'no_admitido') return MENSAJE_FORMATO;
  if (clase === 'pdf') {
    if (yaHayFotos) return MENSAJE_PDF_CON_FOTOS;
    return archivo.size > MAX_BYTES_PDF ? MENSAJE_PDF_GRANDE : null;
  }
  return archivo.size > MAX_BYTES_FOTO_ORIGINAL ? MENSAJE_FOTO_GRANDE : null;
}

/** ¿Se puede sumar un archivo ahora? No mientras algo esté en curso, ni con el máximo de páginas. */
function admiteOtroArchivo(seleccion: SeleccionComprobante, ocupado: boolean): boolean {
  if (ocupado) return false;
  if (seleccion.tipo === 'imagenes') return seleccion.paginas.length < MAX_PAGINAS_COMPROBANTE;
  return seleccion.tipo === 'ninguno' || seleccion.tipo === 'despues';
}

/** Valida y prepara el archivo elegido: un PDF queda seleccionado; una foto pasa al ajuste. */
function useRecepcionDeArchivo(yaHayFotos: boolean, cambiarSeleccion: (seleccion: SeleccionComprobante) => void) {
  const [fotoEnAjuste, setFotoEnAjuste] = useState<Blob | null>(null);
  const [procesando, setProcesando] = useState(false);
  const [error, setError] = useState('');

  const recibirArchivo = useCallback(async (archivo: File) => {
    setError('');
    const clase = clasificarArchivo(archivo);
    const rechazo = motivoDeRechazo(archivo, clase, yaHayFotos);
    if (rechazo) { setError(rechazo); return; }
    if (clase === 'pdf') {
      cambiarSeleccion({ tipo: 'pdf', archivo, protegido: await pdfProtegido(archivo) });
      return;
    }
    setProcesando(true);
    try {
      setFotoEnAjuste(await prepararFotoParaAjuste(archivo));
    } catch {
      setError(MENSAJE_FOTO_ILEGIBLE);
    } finally {
      setProcesando(false);
    }
  }, [yaHayFotos, cambiarSeleccion]);

  return { recibirArchivo, fotoEnAjuste, setFotoEnAjuste, procesando, error, setError };
}

/** Ctrl+V de una captura en cualquier parte del formulario. El texto se pega normal en su campo. */
function usePegarArchivo(activo: boolean, onArchivo: (archivo: File) => void): void {
  useEffect(() => {
    if (!activo) return undefined;
    const alPegar = (e: ClipboardEvent) => {
      const archivo = Array.from(e.clipboardData?.files ?? []).find((f) => clasificarArchivo(f) !== 'no_admitido');
      if (!archivo) return;
      e.preventDefault();
      onArchivo(archivo);
    };
    window.addEventListener('paste', alPegar);
    return () => { window.removeEventListener('paste', alPegar); };
  }, [activo, onArchivo]);
}

const EncabezadoDelBloque: React.FC<{
  readonly titulo: string;
  readonly ayuda?: string;
  readonly exigible: boolean;
}> = ({ titulo, ayuda, exigible }) => (
  <div>
    <p className={LABEL_TEXT}>
      {titulo}
      {exigible
        ? <span className="text-red-500 ml-1">*</span>
        : <span className="text-slate-400 font-normal ml-1">(opcional)</span>}
    </p>
    {ayuda && <p className="text-xs text-slate-500 mt-0.5">{ayuda}</p>}
  </div>
);

export const ComprobanteInput: React.FC<ComprobanteInputProps> = ({
  comprobante, titulo = 'Comprobante', ayuda, deshabilitado = false,
}) => {
  const { seleccion, cambiarSeleccion } = comprobante;
  const { recibirArchivo, fotoEnAjuste, setFotoEnAjuste, procesando, error, setError } = useRecepcionDeArchivo(
    seleccion.tipo === 'imagenes', cambiarSeleccion,
  );
  const esTactil = useMemo(() => window.matchMedia('(pointer: coarse)').matches, []);

  const paginas: readonly PaginaComprobante[] = seleccion.tipo === 'imagenes' ? seleccion.paginas : [];
  const bloqueado = deshabilitado || comprobante.subiendo;
  const aceptaArchivo = admiteOtroArchivo(seleccion, bloqueado || fotoEnAjuste !== null || procesando);

  usePegarArchivo(aceptaArchivo, recibirArchivo);

  const agregarPagina = (blob: Blob) => {
    if (blob.size > MAX_BYTES_PAGINA_PROCESADA) {
      setFotoEnAjuste(null);
      setError(MENSAJE_PAGINA_PESADA);
      return;
    }
    // La página se arma antes de cerrar el ajuste: si algo falla, el error lo muestra el propio ajuste.
    const nueva: PaginaComprobante = { id: nuevoIdDePagina(), blob, vistaPrevia: URL.createObjectURL(blob) };
    setFotoEnAjuste(null);
    cambiarSeleccion({ tipo: 'imagenes', paginas: [...paginas, nueva] });
  };

  const quitarPagina = (id: string) => {
    const pagina = paginas.find((p) => p.id === id);
    if (pagina) URL.revokeObjectURL(pagina.vistaPrevia);
    const restantes = paginas.filter((p) => p.id !== id);
    cambiarSeleccion(restantes.length > 0 ? { tipo: 'imagenes', paginas: restantes } : { tipo: 'ninguno' });
  };

  const quitarDocumento = () => {
    setError('');
    cambiarSeleccion({ tipo: 'ninguno' });
  };

  return (
    <section className="space-y-3" aria-label={titulo}>
      <EncabezadoDelBloque titulo={titulo} ayuda={ayuda} exigible={comprobante.exigible} />

      {fotoEnAjuste && (
        <AjusteFotoComprobante foto={fotoEnAjuste} onListo={agregarPagina} onCancelar={() => { setFotoEnAjuste(null); }} />
      )}

      {procesando && <Progreso texto="Procesando la foto…" />}
      {comprobante.subiendo && <Progreso texto="Subiendo el comprobante…" />}

      <SeleccionElegida
        seleccion={seleccion}
        onQuitarPagina={quitarPagina}
        onQuitarDocumento={quitarDocumento}
        deshabilitado={bloqueado}
      />

      {aceptaArchivo && (
        <SelectorDeArchivo
          esTactil={esTactil}
          agregando={seleccion.tipo === 'imagenes'}
          onArchivo={(archivo) => { recibirArchivo(archivo); }}
        />
      )}

      <AlternativasAlArchivo comprobante={comprobante} bloqueado={bloqueado} enAjuste={fotoEnAjuste !== null} />

      {error && <p className={ERROR_TEXT} role="alert">{error}</p>}
    </section>
  );
};

interface AlternativasAlArchivoProps {
  readonly comprobante: ComprobanteFormulario;
  readonly bloqueado: boolean;
  /** Con una foto en ajuste no se ofrece reutilizar otro comprobante. */
  readonly enAjuste: boolean;
}

/** Mientras no hay archivo: reutilizar un comprobante ya subido o dejarlo para después. */
const AlternativasAlArchivo: React.FC<AlternativasAlArchivoProps> = ({ comprobante, bloqueado, enAjuste }) => {
  const { seleccion, cambiarSeleccion, entidad } = comprobante;
  const [mostrarRecientes, setMostrarRecientes] = useState(false);
  const sinArchivo = seleccion.tipo === 'ninguno' || seleccion.tipo === 'despues';

  const elegirReciente = (reciente: ComprobanteReciente) => {
    setMostrarRecientes(false);
    cambiarSeleccion({ tipo: 'existente', comprobante: reciente });
  };

  return (
    <>
      {sinArchivo && !enAjuste && entidad && (
        <button
          type="button"
          onClick={() => { setMostrarRecientes(true); }}
          disabled={bloqueado}
          className="min-h-11 text-sm font-medium text-blue-600 hover:text-blue-800 disabled:opacity-50"
        >
          Usar un comprobante ya subido
        </button>
      )}

      {comprobante.puedeSubirDespues && sinArchivo && (
        <Checkbox
          label="Subir el comprobante después"
          description="Tendrás 72 horas para adjuntarlo."
          checked={seleccion.tipo === 'despues'}
          onChange={(marcado) => { cambiarSeleccion(marcado ? { tipo: 'despues' } : { tipo: 'ninguno' }); }}
          disabled={bloqueado}
        />
      )}

      {mostrarRecientes && entidad && (
        <ComprobantesRecientesDialog
          entidad={entidad}
          onElegir={elegirReciente}
          onClose={() => { setMostrarRecientes(false); }}
        />
      )}
    </>
  );
};

interface SeleccionElegidaProps {
  readonly seleccion: SeleccionComprobante;
  readonly onQuitarPagina: (id: string) => void;
  readonly onQuitarDocumento: () => void;
  readonly deshabilitado: boolean;
}

/** Lo ya elegido: páginas de foto, PDF o un comprobante reutilizado. */
const SeleccionElegida: React.FC<SeleccionElegidaProps> = ({ seleccion, onQuitarPagina, onQuitarDocumento, deshabilitado }) => {
  if (seleccion.tipo === 'imagenes') {
    return (
      <>
        <MiniaturasPaginas paginas={seleccion.paginas} onQuitar={onQuitarPagina} deshabilitado={deshabilitado} />
        {seleccion.paginas.length >= MAX_PAGINAS_COMPROBANTE && <p className="text-xs text-slate-500">{MENSAJE_MAX_PAGINAS}</p>}
      </>
    );
  }
  if (seleccion.tipo === 'pdf') {
    return (
      <FichaDocumento
        titulo={seleccion.archivo.name}
        aviso={seleccion.protegido ? 'Este PDF está protegido con contraseña: quien lo revise no podrá abrirlo.' : undefined}
        onQuitar={onQuitarDocumento}
        deshabilitado={deshabilitado}
      />
    );
  }
  if (seleccion.tipo === 'existente') {
    const [primero] = seleccion.comprobante.movimientos;
    return (
      <FichaDocumento
        titulo={`Comprobante del ${formatDate(seleccion.comprobante.created_at)}`}
        detalle={primero ? `Ya respalda ${formatCurrency(primero.monto)}` : undefined}
        onQuitar={onQuitarDocumento}
        deshabilitado={deshabilitado}
      />
    );
  }
  return null;
};

const Progreso: React.FC<{ readonly texto: string }> = ({ texto }) => (
  <p role="status" className="flex items-center gap-2 text-sm text-slate-600">
    <Loader2 className="w-4 h-4 motion-safe:animate-spin" aria-hidden="true" /> {texto}
  </p>
);

interface SelectorDeArchivoProps {
  readonly esTactil: boolean;
  readonly agregando: boolean;
  readonly onArchivo: (archivo: File) => void;
}

const SelectorDeArchivo: React.FC<SelectorDeArchivoProps> = ({ esTactil, agregando, onArchivo }) => {
  const inputArchivoRef = useRef<HTMLInputElement>(null);
  const inputCamaraRef = useRef<HTMLInputElement>(null);
  const [arrastrando, setArrastrando] = useState(false);
  const alElegir = (e: React.ChangeEvent<HTMLInputElement>) => {
    const archivo = e.target.files?.[0];
    e.target.value = '';
    if (archivo) onArchivo(archivo);
  };

  const entradas = (
    <>
      <input ref={inputArchivoRef} type="file" accept={ACEPTA} className="hidden"
        aria-label="Elegir el archivo del comprobante" onChange={alElegir} />
      <input ref={inputCamaraRef} type="file" accept="image/*" capture="environment" className="hidden"
        aria-label="Tomar una foto del comprobante" onChange={alElegir} />
    </>
  );

  if (esTactil || agregando) {
    return (
      <div className="flex flex-wrap gap-2">
        {entradas}
        <button type="button" className={BOTON} onClick={() => { inputCamaraRef.current?.click(); }}>
          <Camera className="w-5 h-5" aria-hidden="true" /> {agregando ? 'Fotografiar otra página' : 'Tomar foto'}
        </button>
        <button type="button" className={BOTON} onClick={() => { inputArchivoRef.current?.click(); }}>
          {agregando ? <ImagePlus className="w-5 h-5" aria-hidden="true" /> : <Upload className="w-5 h-5" aria-hidden="true" />}
          {agregando ? 'Agregar otra página' : 'Elegir archivo'}
        </button>
      </div>
    );
  }

  return (
    <>
      {entradas}
      <button
        type="button"
        onClick={() => { inputArchivoRef.current?.click(); }}
        onDragOver={(e) => { e.preventDefault(); setArrastrando(true); }}
        onDragLeave={() => { setArrastrando(false); }}
        onDrop={(e) => {
          e.preventDefault();
          setArrastrando(false);
          const archivo = e.dataTransfer.files[0];
          if (archivo) onArchivo(archivo);
        }}
        className={`w-full min-h-24 rounded-xl border-2 border-dashed px-4 py-6 text-sm text-slate-600 transition-colors ${
          arrastrando ? 'border-blue-400 bg-blue-50' : 'border-slate-300 hover:border-slate-400'
        }`}
      >
        <Upload className="w-6 h-6 mx-auto mb-2 text-slate-400" aria-hidden="true" />
        Arrastra aquí la foto o el PDF, haz clic para elegirlo, o pega una captura con Ctrl+V
      </button>
    </>
  );
};

interface MiniaturasPaginasProps {
  readonly paginas: readonly PaginaComprobante[];
  readonly onQuitar: (id: string) => void;
  readonly deshabilitado: boolean;
}

const MiniaturasPaginas: React.FC<MiniaturasPaginasProps> = ({ paginas, onQuitar, deshabilitado }) => (
  <ol className="flex flex-wrap gap-3">
    {paginas.map((pagina, indice) => (
      <li key={pagina.id} className="relative">
        <img src={pagina.vistaPrevia} alt={`Página ${indice + 1} del comprobante`}
          className="w-20 h-28 object-cover rounded-lg border border-slate-200" />
        <button
          type="button"
          onClick={() => { onQuitar(pagina.id); }}
          disabled={deshabilitado}
          aria-label={`Quitar la página ${indice + 1}`}
          className="absolute -top-2 -right-2 min-h-11 min-w-11 flex items-center justify-center"
        >
          <span className="w-6 h-6 rounded-full bg-white border border-slate-300 shadow flex items-center justify-center">
            <X className="w-3.5 h-3.5 text-slate-600" aria-hidden="true" />
          </span>
        </button>
        <span className="block text-center text-xs text-slate-500 mt-1">Pág. {indice + 1}</span>
      </li>
    ))}
  </ol>
);

interface FichaDocumentoProps {
  readonly titulo: string;
  readonly detalle?: string;
  readonly aviso?: string;
  readonly onQuitar: () => void;
  readonly deshabilitado: boolean;
}

const FichaDocumento: React.FC<FichaDocumentoProps> = ({ titulo, detalle, aviso, onQuitar, deshabilitado }) => (
  <div className="flex items-start gap-3 rounded-xl border border-slate-200 p-3">
    <FileText className="w-8 h-8 text-slate-500 shrink-0" aria-hidden="true" />
    <div className="min-w-0 flex-1">
      <p className="text-sm font-medium text-slate-800 truncate">{titulo}</p>
      {detalle && <p className="text-xs text-slate-500">{detalle}</p>}
      {aviso && <p className="text-xs text-amber-700 mt-1">{aviso}</p>}
    </div>
    <button type="button" onClick={onQuitar} disabled={deshabilitado}
      className="min-h-11 px-2 text-sm text-slate-500 hover:text-slate-700 disabled:opacity-50">
      Quitar
    </button>
  </div>
);
